import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rolesApi, type UserRoleDto } from '../../api/roles';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';
import { useReportScreen } from '../../components/ScreenContext';

const MODULES = [
  'journal-entries', 'invoices', 'invoices-received', 'bank-operations',
  'cash-operations', 'offsets', 'advance-reports', 'persons', 'accounts',
  'tax-rates', 'catalog', 'fixed-assets', 'inventory', 'payroll',
  'reports', 'settings', 'periods', 'audit',
];

const MODULE_LABELS: Record<string, string> = {
  'journal-entries': 'Žurnalo įrašai',
  'invoices': 'Pardavimų SF',
  'invoices-received': 'Pirkimų SF',
  'bank-operations': 'Banko operacijos',
  'cash-operations': 'Kasos operacijos',
  'offsets': 'Įskaitos',
  'advance-reports': 'Avansinės ataskaitos',
  'persons': 'Kontaktai',
  'accounts': 'Sąskaitos',
  'tax-rates': 'Mokesčių tarifai',
  'catalog': 'Katalogas',
  'fixed-assets': 'Ilgalaikis turtas',
  'inventory': 'Atsargos',
  'payroll': 'Darbo užmokestis',
  'reports': 'Ataskaitos',
  'settings': 'Nustatymai',
  'periods': 'Laikotarpiai',
  'audit': 'Auditas',
};

const ACTIONS = ['canView', 'canAdd', 'canEdit', 'canDelete'] as const;
const ACTION_LABELS = { canView: 'Matyti', canAdd: 'Kurti', canEdit: 'Redaguoti', canDelete: 'Trinti' };

export default function RoleManager() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editingRole, setEditingRole] = useState<UserRoleDto | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data: roles, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: rolesApi.list,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => rolesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.addToast('Rolė ištrinta', 'success');
    },
    onError: (err: Error) => toast.addToast(`Klaida: ${err.message}`, 'error'),
  });

  // AI screen context (role permission matrices exceed DOM table scan caps)
  const roleCount = roles?.length ?? 0;
  useReportScreen(`Roles (${roleCount})`, {
    recordCount: roleCount,
    preview: (roles ?? []).slice(0, 8).map(role => {
      const total = MODULES.length;
      const granted = (a: 'canView' | 'canAdd' | 'canEdit' | 'canDelete') =>
        (role.permissions ?? []).filter(p => p[a]).length;
      const line = `${role.roleName}${role.isSystem ? ' [sistemine]' : ''}: ` +
        `Matyti=${granted('canView')}/${total}, Kurti=${granted('canAdd')}/${total}, ` +
        `Redaguoti=${granted('canEdit')}/${total}, Trinti=${granted('canDelete')}/${total}`;
      return line.length > 120 ? line.substring(0, 120) : line;
    }),
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Naudotojų rolės ir teisės</h1>
        <button
          className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
          onClick={() => { setShowCreate(true); setEditingRole(null); }}
        >
          + Nauja rolė
        </button>
      </div>

      {(showCreate || editingRole) && (
        <RoleEditor
          role={editingRole}
          onSave={async (data) => {
            if (editingRole) {
              await rolesApi.update(editingRole.id, data);
              toast.addToast('Rolė atnaujinta', 'success');
            } else {
              await rolesApi.create(data);
              toast.addToast('Rolė sukurta', 'success');
            }
            queryClient.invalidateQueries({ queryKey: ['roles'] });
            setEditingRole(null);
            setShowCreate(false);
          }}
          onCancel={() => { setEditingRole(null); setShowCreate(false); }}
        />
      )}

      {/* Role list */}
      <div className="space-y-4">
        {roles?.map(role => (
          <div key={role.id} className="bg-white rounded-lg border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-gray-900">{role.roleName}</h3>
                {role.description && <p className="text-xs text-gray-500">{role.description}</p>}
                {role.isSystem && <span className="text-xs bg-primary-100 text-primary-800 px-1.5 py-0.5 rounded">Sisteminė</span>}
              </div>
              <div className="flex gap-2">
                <button className="text-xs text-primary-600 hover:underline"
                  onClick={() => { setEditingRole(role); setShowCreate(false); }}>
                  Redaguoti
                </button>
                {!role.isSystem && (
                  <button className="text-xs text-red-500 hover:underline"
                    onClick={() => { if (confirm(`Ištrinti rolę „${role.roleName}“?`)) deleteMutation.mutate(role.id); }}>
                    Ištrinti
                  </button>
                )}
              </div>
            </div>
            <div className="px-4 py-2">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-1 font-medium text-gray-500">Modulis</th>
                    {ACTIONS.map(a => (
                      <th key={a} className="text-center py-1 font-medium text-gray-500 w-16">{ACTION_LABELS[a]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map(mod => {
                    const perm = role.permissions?.find(p => p.module === mod);
                    return (
                      <tr key={mod} className="border-b border-gray-50">
                        <td className="py-0.5">{MODULE_LABELS[mod] ?? mod}</td>
                        {ACTIONS.map(a => (
                          <td key={a} className="text-center py-0.5">
                            {perm?.[a] ? (
                              <span className="text-green-600">&#10003;</span>
                            ) : (
                              <span className="text-gray-300">&#10005;</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        {(!roles || roles.length === 0) && (
          <div className="text-center text-gray-400 py-8">Kol kas nėra apibrėžtų rolių</div>
        )}
      </div>
    </div>
  );
}

function RoleEditor({ role, onSave, onCancel }: {
  role: UserRoleDto | null;
  onSave: (data: { roleName: string; description?: string; permissions: Array<{ module: string; canView: boolean; canAdd: boolean; canEdit: boolean; canDelete: boolean }> }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(role?.roleName ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [perms, setPerms] = useState<Record<string, Record<string, boolean>>>(() => {
    const initial: Record<string, Record<string, boolean>> = {};
    for (const mod of MODULES) {
      const existing = role?.permissions?.find(p => p.module === mod);
      initial[mod] = {
        canView: existing?.canView ?? false,
        canAdd: existing?.canAdd ?? false,
        canEdit: existing?.canEdit ?? false,
        canDelete: existing?.canDelete ?? false,
      };
    }
    return initial;
  });
  const [saving, setSaving] = useState(false);

  const togglePerm = (mod: string, action: string) => {
    setPerms(prev => ({
      ...prev,
      [mod]: { ...(prev[mod] ?? {}), [action]: !(prev[mod]?.[action] ?? false) },
    }));
  };

  const toggleAllForModule = (mod: string, value: boolean) => {
    setPerms(prev => ({
      ...prev,
      [mod]: { canView: value, canAdd: value, canEdit: value, canDelete: value },
    }));
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900 mb-3">{role ? 'Redaguoti rolę' : 'Nauja rolė'}</h2>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Rolės pavadinimas *</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Aprašymas</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>

      <table className="w-full text-xs mb-4">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-1 font-medium text-gray-500">Modulis</th>
            {ACTIONS.map(a => (
              <th key={a} className="text-center py-1 font-medium text-gray-500 w-16">{ACTION_LABELS[a]}</th>
            ))}
            <th className="w-16"></th>
          </tr>
        </thead>
        <tbody>
          {MODULES.map(mod => (
            <tr key={mod} className="border-b border-gray-50">
              <td className="py-1">{MODULE_LABELS[mod] ?? mod}</td>
              {ACTIONS.map(a => (
                <td key={a} className="text-center py-1">
                  <input type="checkbox"
                    checked={perms[mod]?.[a] ?? false}
                    onChange={() => togglePerm(mod, a)} />
                </td>
              ))}
              <td className="text-center">
                <button className="text-xs text-gray-400 hover:text-gray-600"
                  onClick={() => toggleAllForModule(mod, !perms[mod]?.canView)}>
                  Perjungti
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex gap-2">
        <button
          className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          onClick={async () => {
            setSaving(true);
            try {
              await onSave({
                roleName: name,
                description: description || undefined,
                permissions: MODULES.map(mod => ({
                  module: mod,
                  canView: perms[mod]?.canView ?? false,
                  canAdd: perms[mod]?.canAdd ?? false,
                  canEdit: perms[mod]?.canEdit ?? false,
                  canDelete: perms[mod]?.canDelete ?? false,
                })),
              });
            } catch (err) { alert(err instanceof Error ? err.message : 'Klaida'); }
            setSaving(false);
          }}
          disabled={saving || !name}
        >
          {saving ? 'Išsaugoma…' : (role ? 'Atnaujinti rolę' : 'Sukurti rolę')}
        </button>
        <button className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50" onClick={onCancel}>
          Atšaukti
        </button>
      </div>
    </div>
  );
}
